import { useEffect } from "react";

/**
 * seo.js
 * ------------------------------------------------------------------
 * Small shared helpers for pages that need to control what search
 * engines and link-unfurlers (Google, Facebook, WhatsApp, Twitter,
 * Slack, etc.) see when they crawl a page.
 *
 * Robots meta:
 *   Sets (and cleans up) a <meta name="robots"> tag. Content re-applies
 *   whenever it changes, so a page that only learns its indexability
 *   after an API call resolves (e.g. "index only if this merchant is
 *   Pro") can start as noindex and flip once the data is in -- crawlers
 *   and our prerender service always see the FINAL value, since they
 *   wait for markPrerenderReady() before taking their snapshot.
 *
 * Prerender-ready signal:
 *   Our nginx config proxies known bots (Googlebot, facebookexternalhit,
 *   WhatsApp, Twitterbot, etc.) to a headless-Chrome "prerender" service
 *   that renders the page and returns static HTML. It needs to know when
 *   it's safe to snapshot -- otherwise it may capture the page before an
 *   API call resolves and the real title/description/OG tags are set.
 * ------------------------------------------------------------------
 */
export function useRobotsMeta(content = "noindex, nofollow") {
  useEffect(() => {
    let tag = document.querySelector('meta[name="robots"]');
    const existed = !!tag;
    const previousContent = tag?.getAttribute("content") ?? null;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
    return () => {
      if (!existed) {
        tag.remove();
      } else {
        tag.setAttribute("content", previousContent);
      }
    };
  }, [content]);
}

export function markPrerenderReady() {
  if (typeof window !== "undefined") {
    window.prerenderReady = true;
  }
}